import React, { useEffect, useMemo, useState } from 'react';

import styled from 'styled-components';

import { Permissions } from 'src/app_deprecated/constants/PermissionsConstants';

import { InfoBanner } from 'src/app/components/banner/info';
import { Button } from 'src/app/components/lib/button';
import { Drawer, DrawerContent, DrawerHeader } from 'src/app/components/lib/drawer';
import { Heading } from 'src/app/components/lib/heading';
import { EntityNameSection } from 'src/app/components/lib/query-builder/entity-name-section';
import { QueryBuilder } from 'src/app/components/lib/query-builder/index';
import { StyledQueryBuilderDrawerContent } from 'src/app/components/lib/query-builder/query-builder.styles';
import { validateAllRules } from 'src/app/components/lib/query-builder/use-query-builder/validator';
import { FixedHeightDataGrid } from 'src/app/components/lib/table';
import { useGetSmartTagsQuery } from 'src/app/queries/inventory/smart-tags/get';
import { useGetTagDetailsQuery } from 'src/app/queries/inventory/smart-tags/get-details';
import { usePreviewSmartTagPackagesQuery } from 'src/app/queries/inventory/smart-tags/preview-packages';
import { useSaveSmartTagMutation } from 'src/app/queries/inventory/smart-tags/save';
import { useNotificationActions } from 'src/app/state/notifications';
import { usePermissionCheck } from 'src/app/state/user-permissions';

import { useColumns } from './use-columns';

import type { InitialWizardState } from '../types';
import type { ClientError } from 'graphql-request';
import type { RuleGroup } from 'src/app/components/lib/query-builder/use-query-builder';

type SmartTagWizardProps = {
  initialState?: InitialWizardState;
  isWizardActive: boolean;
  onClose: () => void;
  smartTagId?: number;
};

export function SmartTagWizard({ initialState, isWizardActive, onClose, smartTagId }: SmartTagWizardProps) {
  const notifications = useNotificationActions();
  const userCanEditSmartTags = usePermissionCheck(Permissions.EditTags);

  // Note: tagId will be null when creating a new tag. The API accepts null and returns
  // the schema needed for the QueryBuilder. We only gate the query on isWizardActive
  // (vs. isWizardActive && smartTagId) to prevent unnecessary API calls when the wizard is closed
  // without inadvertently blocking new tag creation.
  const { data: tagDetails, isLoading: isTagDetailsLoading } = useGetTagDetailsQuery({
    tagId: smartTagId ?? null,
    enabled: isWizardActive,
  });

  const { data: existingSmartTags = [] } = useGetSmartTagsQuery({
    enabled: isWizardActive,
  });

  const { mutate: saveSmartTagMutator, isLoading: isSaveSmartTagLoading } = useSaveSmartTagMutation();

  const [smartTagName, setSmartTagName] = useState(initialState?.name ?? tagDetails?.name ?? '');
  const [isDirty, setIsDirty] = useState(false);
  const [previewRuleGroup, setPreviewRuleGroup] = useState<RuleGroup | null>(initialState?.ruleGroup ?? null);

  const nameValidationError = useMemo(() => {
    if (!smartTagName.trim()) {
      return undefined;
    }

    const isDuplicateName = existingSmartTags.some(
      (tag) => tag.Name.trim().toLowerCase() === smartTagName.trim().toLowerCase() && tag.Id !== smartTagId
    );

    return isDuplicateName ? 'A smart tag with this name already exists' : undefined;
  }, [smartTagName, existingSmartTags, smartTagId]);

  const isRuleGroupValid = useMemo(() => {
    if (!previewRuleGroup || !tagDetails?.schema) {
      return false;
    }
    return validateAllRules({ ruleGroup: previewRuleGroup, schema: tagDetails.schema });
  }, [previewRuleGroup, tagDetails]);

  const previewEnabled = useMemo(() => {
    const hasRules = Boolean(previewRuleGroup?.rules && previewRuleGroup.rules.length > 0);
    return hasRules && isRuleGroupValid;
  }, [previewRuleGroup, isRuleGroupValid]);

  const { data: previewPackages = [], isFetching: isPreviewPackagesLoading } = usePreviewSmartTagPackagesQuery({
    Id: smartTagId ?? null,
    IsEnabled: previewEnabled,
    RuleGroup: previewRuleGroup ?? { combinator: 'and', rules: [] },
  });

  useEffect(() => {
    if (tagDetails?.name) {
      setSmartTagName(tagDetails.name);
    }
  }, [tagDetails]);

  // Reset and apply initial state when wizard becomes active or smartTagId changes
  useEffect(() => {
    if (isWizardActive) {
      // Reset state to initial values
      setSmartTagName(initialState?.name ?? tagDetails?.name ?? '');
      setIsDirty(false);
      setPreviewRuleGroup(initialState?.ruleGroup ?? null);
    }
  }, [isWizardActive, smartTagId, initialState, tagDetails]);

  const handleOnClose = () => {
    onClose();
  };

  const handleOnSave = (ruleGroup: RuleGroup) => {
    saveSmartTagMutator(
      {
        Id: smartTagId ?? null,
        Name: smartTagName,
        RuleGroup: ruleGroup,
      },
      {
        onSuccess: () => {
          notifications.success({ message: 'Smart tag saved successfully' });
          handleOnClose();
        },
        onError: (e) => {
          const error = e as ClientError;
          const { data, status } = error.response;
          if (status === 400) {
            notifications.error({ message: data.Message });
          }
        },
      }
    );
  };

  const previewColumns = useColumns();

  const showPreviewWarning = useMemo(
    () => previewEnabled && previewPackages.length >= 1000,
    [previewEnabled, previewPackages.length]
  );

  return (
    <Drawer open={isWizardActive} onClose={handleOnClose}>
      <DrawerHeader>
        <Heading>{tagDetails?.name ?? 'New smart tag'}</Heading>
        <Button label='Close' variant='secondary' onClick={handleOnClose} />
      </DrawerHeader>
      <ContentContainer>
        <StyledQueryBuilderDrawerContent $width='60%'>
          <EntityNameSection
            headerSubHeaderCopy='This will be the name of the tag that will be applied to the products that match the criteria defined below.'
            headerTitle='Smart tag name'
            inputDisabled={!userCanEditSmartTags}
            inputLabel='Name:'
            inputPlaceholder='Smart tag name'
            inputValidationError={nameValidationError}
            inputValue={smartTagName}
            setInputValue={(value) => {
              setSmartTagName(value);
              setIsDirty(true);
            }}
          />

          {tagDetails && (
            <QueryBuilder
              disabled={!userCanEditSmartTags}
              disableSaveButton={smartTagName.length === 0 || !!nameValidationError}
              isLoading={isTagDetailsLoading || isSaveSmartTagLoading}
              isQueryBuilderDirty={isDirty}
              previewQuery={setPreviewRuleGroup}
              queryRules={tagDetails.details}
              saveQuery={handleOnSave}
              schema={tagDetails.schema}
              setIsQueryBuilderDirty={setIsDirty}
              titleCopy='Add products that meet the following conditions:'
              titleCopyDisabled='Products that meet the following conditions will have this tag applied'
            />
          )}
        </StyledQueryBuilderDrawerContent>

        <Sidebar>
          {showPreviewWarning && (
            <InfoBanner copy='Only the first 1000 eligible packages are included in the preview.' />
          )}
          <FixedHeightDataGrid
            columns={previewColumns}
            emptyStateContainerHeight='100%'
            getRowId={(row) => row.PackageId}
            height='100%'
            hideActionsColumn
            loading={isPreviewPackagesLoading}
            name='smart-tag-detail-packages-preview'
            noResultsHeading='No results'
            noResultsSubheading='No packages match the rules for this smart tag'
            rows={previewEnabled ? previewPackages : []}
          />
        </Sidebar>
      </ContentContainer>
    </Drawer>
  );
}

const Sidebar = styled.div`
  flex-grow: 1;
  width: 40%;
  background: var(--color-brand-primary-white);
  border-left: 1px solid var(--color-gray-20);
  overflow: auto;
  padding: var(--sizes-50);
  margin-bottom: 85px;

  & .MuiDataGrid-virtualScroller::-webkit-scrollbar {
    display: none;
  }
`;

const ContentContainer = styled(DrawerContent)`
  display: flex;
  flex-direction: row;
  overflow: scroll;
`;
